import React, { useState, useEffect, useRef, useCallback } from 'react';
import SearchSvg from '@src/assets/svg/search.svg';
import { useI18n } from '../i18n/I18nContext';
import { performSearch, isBrowserExtensionMode } from '../services/browserAPI';

// Define search engine interface
interface SearchEngine {
  name: string;
  displayName: string;
  searchUrl: string;
}

interface SearchInputProps {
  show: boolean;
  onAIChat?: (message: string) => void;
}

const SEARCH_ENGINES: readonly SearchEngine[] = [
  { name: 'google', displayName: 'Google', searchUrl: 'https://www.google.com/search?q=' },
  { name: 'baidu', displayName: 'Baidu', searchUrl: 'https://www.baidu.com/s?wd=' },
  { name: 'bing', displayName: 'Bing', searchUrl: 'https://www.bing.com/search?q=' },
  { name: 'duckduckgo', displayName: 'DuckDuckGo', searchUrl: 'https://duckduckgo.com/?q=' },
  { name: 'yahoo', displayName: 'Yahoo!', searchUrl: 'https://search.yahoo.com/search?p=' },
  { name: 'yandex', displayName: 'Yandex', searchUrl: 'https://yandex.com/search/?text=' },
];

const getPreferredSearchEngine = (): SearchEngine => {
  const savedEngine = localStorage.getItem('preferredSearchEngine');
  return SEARCH_ENGINES.find(engine => engine.name === savedEngine) ?? SEARCH_ENGINES[0];
};

const SearchInput: React.FC<SearchInputProps> = ({ show, onAIChat }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();
  const isExtensionMode = isBrowserExtensionMode();

  // State for current search engine
  const [currentSearchEngine, setCurrentSearchEngine] = useState<SearchEngine>(getPreferredSearchEngine);

  useEffect(() => {
    if (!show) setDropdownOpen(false);
  }, [show]);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    if (!isExtensionMode) {
      setDropdownOpen(!dropdownOpen);
    } else {
      handleSearchEngineClick();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Function to change search engine
  const changeSearchEngine = (engine: SearchEngine, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    setCurrentSearchEngine(engine);
    localStorage.setItem('preferredSearchEngine', engine.name);
    setDropdownOpen(false); // Close dropdown after selection
  };

  // Handle redirection to browser settings for extensions
  const handleSearchEngineClick = () => {
    if (isExtensionMode) {
      if (typeof chrome !== 'undefined' && chrome.tabs && chrome.tabs.create) {
        chrome.tabs.create({ url: 'chrome://settings/searchEngines' });
      } else {
        // 回退方案，虽然可能不起作用，但保留作为兼容处理
        window.open('chrome://settings/searchEngines', '_blank');
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Check if the input is a URL
    const query = searchQuery.trim();
    if (isValidUrl(query) || query.includes('.')) {
      let url = query;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }
      
      // Use performSearch for URLs in extension mode to comply with Chrome Store policies
      if (isExtensionMode) {
        performSearch(url);
      } else {
        window.location.href = url;
      }
    } else {
      // For search queries
      if (isExtensionMode) {
        performSearch(query);
      } else {
        window.location.href = `${currentSearchEngine.searchUrl}${encodeURIComponent(query)}`;
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = searchQuery.trim();
      if ((e.ctrlKey || e.metaKey) && onAIChat) {
        onAIChat(query);
        setSearchQuery('');
        return;
      }
      // Handle as normal search
      handleSearch(e as unknown as React.FormEvent);
    }
  };

  // 使用useCallback优化事件处理函数
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <form onSubmit={handleSearch} className="flex justify-center w-full">
      <label className="input flex items-center gap-2 w-[90vw] sm:w-[85vw] md:w-[30rem] backdrop-blur-md bg-white/70 text-black rounded-full">
        {/* Search Engine Icon and Dropdown */}
        <div ref={dropdownRef} className="relative">
          {isExtensionMode ? (
            <img src={SearchSvg} alt="Search" className="h-4 w-4 opacity-70" />
          ) : (
            <>
              <div 
                role="button"
                className="btn btn-ghost btn-circle btn-sm"
                onClick={toggleDropdown}
              >
                {currentSearchEngine.name.charAt(0).toUpperCase()}
              </div>
              
              <div 
                className={`
                  absolute top-full left-0 mt-1 z-[100] 
                  backdrop-blur-md bg-white/70 shadow-lg rounded-lg w-52 py-2
                  transition-all duration-300 ease-in-out
                  ${dropdownOpen 
                    ? 'opacity-100 translate-y-1' 
                    : 'opacity-0 translate-y-0 pointer-events-none'
                  }
                `}
              >
                <ul className="menu text-black">
                  {SEARCH_ENGINES.map((engine) => (
                    <li key={engine.name}>
                      <a 
                        className={engine.name === currentSearchEngine.name ? 'active bg-white/50' : 'hover:bg-black/30'}
                        onClick={(e) => changeSearchEngine(engine, e)}
                      >
                        {engine.displayName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
        
        <input 
          ref={inputRef}
          type="text" 
          className="grow bg-transparent focus:outline-none"
          placeholder={t('buttons.search_placeholder')}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />
      </label>
    </form>
  );
};

export default SearchInput;
